import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AIPanel from "@/components/AIPanel";
import {
  SquaresFour, Target, Phone, Handshake, Megaphone, GraduationCap,
  CurrencyInr, ClipboardText, Gear, Sparkle, Bell, SignOut, Users, Briefcase,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour },
  { to: "/goals", label: "Goal Setting", icon: Target },
  { to: "/telecalling", label: "Telecalling & Leads", icon: Phone },
  { to: "/counselling", label: "Counselling & Admissions", icon: Users },
  { to: "/business-development", label: "Business Development", icon: Handshake },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/batches", label: "Batches & Operations", icon: GraduationCap },
  { to: "/finance", label: "Finance", icon: CurrencyInr },
  { to: "/reviews", label: "Reviews", icon: ClipboardText },
  { to: "/settings", label: "Settings", icon: Gear },
];

const TITLES = Object.fromEntries(NAV.map((n) => [n.to, n.label]));

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const title = TITLES[location.pathname] || "Dr. Awish Institute CRM";

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setAlerts(r.data.insights || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-white flex flex-col fixed h-screen z-40" data-testid="sidebar">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <div className="h-10 w-10 rounded-lg bg-gold flex items-center justify-center shrink-0">
            <Sparkle size={22} weight="fill" className="text-navy" />
          </div>
          <div className="font-heading leading-tight">
            <div className="font-extrabold text-sm">Dr. Awish Institute</div>
            <div className="text-gold text-[10px] font-semibold tracking-[0.18em] uppercase">CRM Suite</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/,"")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-transform duration-200 hover:translate-x-1 ${
                    isActive
                      ? "bg-gold text-navy font-semibold"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon size={19} weight="bold" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <button
          data-testid="ai-open-sidebar"
          onClick={() => setAiOpen(true)}
          className="m-3 rounded-lg bg-gradient-to-r from-gold to-[#b8923f] text-navy font-semibold text-sm px-3 py-3 flex items-center gap-2 hover:-translate-y-[1px] transition-transform duration-200"
        >
          <Briefcase size={18} weight="fill" /> Institute Intelligence
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-8 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate font-semibold">Dr. Awish Aesthetic Institute</p>
            <h1 className="font-heading text-2xl font-extrabold text-navy leading-tight">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="ai-open-header"
              onClick={() => setAiOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-gold/40 bg-gold/5 px-3 py-2 text-sm font-semibold text-navy hover:bg-gold/10 transition-transform duration-200"
            >
              <Sparkle size={16} weight="fill" className="text-gold" /> Ask AI
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notification-bell" className="relative h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50">
                  <Bell size={18} weight="bold" className="text-slate" />
                  {alerts.length > 0 && <span className="absolute top-1.5 right-2 h-2 w-2 rounded-full bg-rose" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-3 py-2 text-xs uppercase tracking-wider text-slate font-semibold border-b border-slate-100">
                  Notifications ({alerts.length})
                </div>
                <div className="max-h-72 overflow-y-auto py-1" data-testid="notifications-list">
                  {alerts.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-slate">All clear — no alerts.</div>
                  ) : (
                    alerts.map((a, i) => (
                      <div key={i} className="px-3 py-2 text-sm text-navy hover:bg-slate-50 flex gap-2">
                        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${a.type === "alert" ? "bg-rose" : a.type === "positive" ? "bg-emerald" : "bg-gold"}`} />
                        {a.text}
                      </div>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-50">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-navy text-white text-xs">
                      {(user?.name || "U").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-navy hidden sm:block">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose">
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-8 animate-fade-up">
          <Outlet />
        </main>
      </div>

      <AIPanel open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
